import { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { api } from '../api/client';
import { useBeginnerMode } from '../hooks/useBeginnerMode';

interface PlanStatus {
  plan: 'trial' | 'friend' | 'active';
  daysLeft: number | null;
  expired: boolean;
}

interface SidebarProps {
  siteId: string;
  sectionLabel?: string; // kept for API compatibility, no longer used
  planStatus?: PlanStatus | null;
}

/* ── Lucide-style SVG icons (18×18, stroke-only) ──────────────────── */
const s = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true as const };

const icons = {
  dashboard:  <svg {...s}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  content:    <svg {...s}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  appearance: <svg {...s}><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="19" cy="13" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="12" cy="19" r="2.5"/><circle cx="12" cy="12" r="3"/></svg>,
  media:      <svg {...s}><path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>,
  seo:        <svg {...s}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  publish:    <svg {...s}><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>,
  messages:   <svg {...s}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  settings:   <svg {...s}><path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/><circle cx="12" cy="12" r="3"/></svg>,
  analytics:  <svg {...s}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
};

const navClass = ({ isActive }: { isActive: boolean }) => `sidebar-link${isActive ? ' active' : ''}`;

export default function Sidebar({ siteId, planStatus }: SidebarProps) {
  const base = `/sites/${siteId}`;
  const { beginnerMode, toggle: toggleBeginnerMode } = useBeginnerMode();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const items = await api.listContent(siteId, 'submission');
      const unread = items.filter(i => !(i.data as Record<string, unknown>).read).length;
      setUnreadCount(unread);
    } catch {
      // Silently fail — badge just won't show
    }
  }, [siteId]);

  useEffect(() => {
    fetchUnreadCount();
    const timer = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(timer);
  }, [fetchUnreadCount]);

  return (
    <aside className="sidebar">
      <nav className="sidebar-nav">

        {/* ── Dashboard ── */}
        <NavLink to={base} end className={navClass}>
          <span className="sidebar-icon">{icons.dashboard}</span>
          Dashboard
        </NavLink>

        {/* ── CONTENT ── */}
        <div className="sidebar-section-label">Content</div>

        <NavLink to={`${base}/content`} className={navClass}>
          <span className="sidebar-icon">{icons.content}</span>
          Content
        </NavLink>

        <NavLink to={`${base}/appearance`} className={navClass}>
          <span className="sidebar-icon">{icons.appearance}</span>
          Design
        </NavLink>

        <NavLink to={`${base}/media`} className={navClass}>
          <span className="sidebar-icon">{icons.media}</span>
          Media
        </NavLink>

        {!beginnerMode && (
          <NavLink to={`${base}/seo`} className={navClass}>
            <span className="sidebar-icon">{icons.seo}</span>
            SEO
          </NavLink>
        )}

        {/* ── PUBLISH ── */}
        <div className="sidebar-section-label">Publish</div>

        <NavLink to={`${base}/publish`} className={navClass}>
          <span className="sidebar-icon">{icons.publish}</span>
          Publish
        </NavLink>

        <NavLink to={`${base}/messages`} className={navClass}>
          <span className="sidebar-icon">{icons.messages}</span>
          Messages
          {unreadCount > 0 && (
            <span className="sidebar-unread-badge" aria-label={`${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`}>{unreadCount}</span>
          )}
        </NavLink>

        {/* ── SETTINGS (advanced only) ── */}
        {!beginnerMode && (
          <>
            <div className="sidebar-section-label">Settings</div>

            <NavLink to={`${base}/domain`} className={navClass}>
              <span className="sidebar-icon">{icons.settings}</span>
              Settings
            </NavLink>

            <NavLink to={`${base}/analytics`} className={navClass}>
              <span className="sidebar-icon">{icons.analytics}</span>
              Analytics
            </NavLink>
          </>
        )}

      </nav>

      {/* ── Mode toggle ── */}
      <div className="sidebar-footer">
        {planStatus && (
          <NavLink to="/billing" className="sidebar-plan-link">
            {planStatus.plan === 'trial' ? (
              <span className={`sidebar-plan-badge ${planStatus.expired ? 'sidebar-plan-badge--expired' : 'sidebar-plan-badge--trial'}`}>
                {planStatus.expired
                  ? 'Trial expired'
                  : planStatus.daysLeft != null
                    ? `Trial · ${planStatus.daysLeft}d left`
                    : 'Free trial'}
              </span>
            ) : (
              <span className="sidebar-plan-badge sidebar-plan-badge--active">
                {planStatus.plan === 'active' ? 'Active plan' : 'Friend plan'}
              </span>
            )}
          </NavLink>
        )}
        <button className="sidebar-mode-toggle" onClick={toggleBeginnerMode} aria-pressed={beginnerMode}>
          {beginnerMode ? (
            <>
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>
              Advanced mode
            </>
          ) : (
            <>
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
              Beginner mode
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
